import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, BeakerIcon, FireIcon } from '@heroicons/react/24/solid';

export default function AIMealPlannerFeature() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-pri-dark text-white">
      {/* Header with navigation back */}
      <header className="bg-pri-dark/90 backdrop-blur-md fixed top-0 w-full z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img
              className="h-10 w-auto"
              src={`${process.env.REACT_APP_CDN_URL}/logo/logo-no-bg.png`}
              alt="Jingleez"
            />
          </Link>
          
          <Link
            to="/"
            className="inline-flex items-center text-sm font-semibold rounded-full px-4 py-2 bg-white/10 hover:bg-white/20 transition-colors"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
        </div>
      </header>

      <main className="pt-24 pb-16">
        {/* Hero section */}
        <section className="relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm mb-4">
                  <BeakerIcon className="h-4 w-4 text-mint-green mr-2" />
                  <span className="text-white text-xs font-medium">Feature Spotlight</span>
                </div>
                
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-soft-pink">
                  AI Meal Planner
                </h1>
                
                <div className="h-1 w-20 bg-mint-green mb-6"></div>
                
                <p className="text-lg sm:text-xl text-pri-gray/90 mb-8 max-w-xl">
                  Personalized meal planning with AI that adapts to your preferences, dietary needs, and schedule.
                </p>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="relative rounded-2xl overflow-hidden shadow-2xl"
              >
                <img 
                  src={`${process.env.REACT_APP_CDN_URL}/illustrations/meal-planner.png`}
                  alt="AI Meal Planner Interface" 
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-pri-dark to-transparent opacity-30"></div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Why we built this section */}
        <section className="py-16 bg-gradient-to-b from-pri-dark to-pri-dark/80">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="max-w-4xl mx-auto"
            >
              <div className="text-center mb-10">
                <h2 className="text-3xl font-bold text-soft-pink">Why We Built This?</h2>
                <div className="h-1 w-20 bg-mint-green mx-auto mt-4 mb-6"></div>
              </div>
              
              <div className="bg-gradient-to-br from-pri-dark/90 to-sec-dark/80 rounded-2xl p-8 border border-white/10 shadow-xl">
                <p className="text-pri-gray text-lg leading-relaxed mb-6">
                  Have you ever found yourself staring into the fridge, wondering what to cook? Or wasting food because you bought ingredients without a clear plan?
                </p>
                
                <p className="text-pri-gray text-lg leading-relaxed mb-6">
                  <span className="text-soft-pink font-medium">Meal planning is challenging, especially when trying to balance nutrition, taste preferences, and dietary restrictions.</span> 
                  Traditional meal planning takes considerable time and expertise, leading many to resort to unhealthy convenience options.
                </p>
                
                <p className="text-pri-gray text-lg leading-relaxed">
                  We built the Jingleez AI Meal Planner to solve these problems—a smart platform that creates 
                  <span className="text-mint-green font-medium"> personalized meal plans tailored to your unique needs</span> and preferences. 
                  Our goal is to help you eat healthier, reduce food waste, and take the stress out of answering "what's for dinner?" every night.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Key benefits section */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.h2 
              className="text-3xl font-bold mb-12 text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Key Benefits
            </motion.h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                {
                  title: "Personalized Nutrition",
                  description: "Get meal plans tailored to your dietary needs, allergies, and health goals."
                },
                {
                  title: "Time Saving",
                  description: "Eliminate hours of meal planning with AI-generated weekly plans and shopping lists."
                },
                {
                  title: "Diverse Recipes",
                  description: "Discover new dishes and cuisines that match your taste preferences and dietary requirements."
                },
                {
                  title: "Reduced Food Waste",
                  description: "Buy only what you need with smart shopping lists that optimize ingredient usage."
                }
              ].map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 * index }}
                  className="bg-gradient-to-br from-pri-dark/80 to-sec-dark/90 p-6 rounded-xl border border-white/10 relative"
                >
                  <h3 className="text-xl font-semibold mb-4 text-mint-green">{benefit.title}</h3>
                  <p className="text-pri-gray">{benefit.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* How it works section */}
        <section className="py-16 bg-gradient-to-b from-pri-dark to-sec-dark">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.h2 
              className="text-3xl font-bold mb-12 text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              How It Works
            </motion.h2>

            <div className="flex flex-col items-center max-w-3xl mx-auto">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="w-full"
              >
                <div className="space-y-12">
                  {/* Step 1 with Illustration */}
                  <div className="bg-gradient-to-br from-pri-dark/80 to-sec-dark/95 rounded-xl overflow-hidden shadow-lg shadow-soft-pink/20">
                    <div className="p-6">
                      <div className="flex items-start mb-6 justify-start">
                        <div className="flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-full bg-mint-green text-pri-dark font-bold">
                          01
                        </div>
                        <div className="ml-4">
                          <h3 className="text-lg font-semibold text-soft-pink">Set Your Preferences</h3>
                          <p className="text-pri-gray mt-1">Tell us about your dietary needs, allergies, and taste preferences.</p>
                        </div>
                      </div>
                      
                      {/* Preference Setting Illustration */}
                      <motion.div 
                        className="mt-4 flex justify-center"
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.3, duration: 0.5 }}
                      >
                        <div className="relative w-full max-w-md bg-pri-dark/60 rounded-xl p-6 shadow-lg border border-white/10 overflow-hidden">
                          {/* Decorative elements */}
                          <div className="absolute top-0 right-0 w-32 h-32 bg-soft-pink/10 rounded-full blur-xl -mr-16 -mt-16"></div>
                          <div className="absolute bottom-0 left-0 w-24 h-24 bg-mint-green/10 rounded-full blur-xl -ml-10 -mb-10"></div>
                          
                          {/* Preferences Form Representation */}
                          <div className="space-y-5">
                            <div className="flex items-center mb-3">
                              <div className="w-3 h-3 rounded-full bg-soft-pink mr-2"></div>
                              <h4 className="text-white text-sm font-medium">Dietary Preferences</h4>
                            </div>
                            
                            <div className="flex flex-wrap gap-2">
                              {["Vegetarian", "Low Carb", "High Protein", "Gluten Free"].map((diet, i) => (
                                <div key={i} className="bg-white/10 px-3 py-1 rounded-full text-xs text-white/80">
                                  {diet}
                                </div>
                              ))}
                            </div>
                            
                            <div className="flex items-center mb-3 mt-5">
                              <div className="w-3 h-3 rounded-full bg-mint-green mr-2"></div>
                              <h4 className="text-white text-sm font-medium">Allergies & Restrictions</h4>
                            </div>
                            
                            <div className="flex flex-wrap gap-2">
                              {["Nuts", "Dairy", "Shellfish"].map((allergy, i) => (
                                <div key={i} className="bg-white/10 px-3 py-1 rounded-full text-xs text-white/80">
                                  {allergy}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    </div>
                  </div>
                  
                  {/* Step 2 with Illustration */}
                  <div className="bg-gradient-to-br from-pri-dark/80 to-sec-dark/95 rounded-xl overflow-hidden shadow-lg shadow-soft-pink/20">
                    <div className="p-6">
                      <div className="flex items-start mb-6 justify-start">
                        <div className="flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-full bg-mint-green text-pri-dark font-bold">
                          02
                        </div>
                        <div className="ml-4">
                          <h3 className="text-lg font-semibold text-soft-pink">Generate Your Meal Plan</h3>
                          <p className="text-pri-gray mt-1">Our AI creates a personalized plan for a day or entire week.</p>
                        </div>
                      </div>
                      
                      {/* Meal Plan Generation Illustration */}
                      <motion.div 
                        className="mt-4 flex justify-center"
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.3, duration: 0.5 }}
                      >
                        <div className="relative w-full max-w-md bg-pri-dark/60 rounded-xl p-6 shadow-lg border border-white/10 overflow-hidden">
                          <div className="absolute top-0 right-0 w-32 h-32 bg-soft-pink/10 rounded-full blur-xl -mr-16 -mt-16"></div>
                          <div className="absolute bottom-0 left-0 w-24 h-24 bg-mint-green/10 rounded-full blur-xl -ml-10 -mb-10"></div>
                          
                          <div className="flex justify-between mb-4">
                            <div className="text-white text-sm font-medium">Monday, August 15</div>
                            <div className="flex gap-2">
                              <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center">
                                <FireIcon className="w-3 h-3 text-soft-pink" />
                              </div>
                              <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center">
                                <span className="text-white text-xs">+</span>
                              </div>
                            </div>
                          </div>
                          
                          <div className="space-y-4">
                            {["Breakfast", "Lunch", "Dinner"].map((meal, i) => (
                              <div key={i} className="bg-white/5 rounded-lg p-3">
                                <div className="text-mint-green text-xs mb-2">{meal}</div>
                                <div className="h-3 w-3/4 bg-white/20 rounded-full"></div>
                                <div className="h-2 w-1/2 bg-white/10 rounded-full mt-2"></div>
                                <div className="mt-2 flex items-center">
                                  <div className="w-8 h-8 rounded-full bg-white/10"></div>
                                  <div className="ml-2">
                                    <div className="h-2 w-16 bg-white/20 rounded-full"></div>
                                    <div className="h-2 w-12 bg-white/10 rounded-full mt-1"></div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </motion.div>
                    </div>
                  </div>
                  
                  {/* Step 3 with Illustration - Calendar View */}
                  <div className="bg-gradient-to-br from-pri-dark/80 to-sec-dark/95 rounded-xl overflow-hidden shadow-lg shadow-soft-pink/20">
                    <div className="p-6">
                      <div className="flex items-start mb-6 justify-start">
                        <div className="flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-full bg-mint-green text-pri-dark font-bold">
                          03
                        </div>
                        <div className="ml-4">
                          <h3 className="text-lg font-semibold text-soft-pink">View in Calendar</h3>
                          <p className="text-pri-gray mt-1">See your meals in a calendar view for easy planning and scheduling.</p>
                        </div>
                      </div>
                      
                      {/* Calendar View Illustration */}
                      <motion.div 
                        className="mt-4 flex justify-center"
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.3, duration: 0.5 }}
                      >
                        <div className="relative w-full max-w-md bg-pri-dark/60 rounded-xl p-6 shadow-lg border border-white/10 overflow-hidden">
                          <div className="absolute top-0 right-0 w-32 h-32 bg-soft-pink/10 rounded-full blur-xl -mr-16 -mt-16"></div>
                          <div className="absolute bottom-0 left-0 w-24 h-24 bg-mint-green/10 rounded-full blur-xl -ml-10 -mb-10"></div>
                          
                          {/* Calendar Navigation */}
                          <div className="flex justify-between items-center mb-2">
                            <div className="flex items-center space-x-2">
                              <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center">
                                <span className="text-white text-xs">◀</span>
                              </div>
                              <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center">
                                <span className="text-white text-xs">▶</span>
                              </div>
                              <button className="bg-white/10 text-white/80 text-xs px-3 py-1 rounded-md">
                                Today
                              </button>
                            </div>
                            <div className="text-white font-medium text-sm">
                              April 27, 2025
                            </div>
                            <div className="flex items-center space-x-1">
                              <div className="bg-white/10 text-white/80 text-xs px-2 py-1 rounded-md flex items-center">
                                <span className="mr-1">◧</span>
                                <span>Events</span>
                              </div>
                              <div className="bg-soft-pink/20 text-white/80 text-xs px-2 py-1 rounded-md flex items-center">
                                <span className="mr-1">🍽</span>
                                <span>Meals</span>
                              </div>
                            </div>
                          </div>
                          
                          {/* Calendar Content */}
                          <div className="mt-3">
                            <div className="text-soft-pink font-medium text-sm mb-2">
                              Meals for April 27, 2025
                            </div>
                            
                            <div className="grid grid-cols-3 gap-3">
                              {/* Breakfast */}
                              <div className="bg-pri-dark/40 border border-white/5 rounded-lg p-3 flex flex-col items-center justify-center">
                                <div className="text-mint-green text-xs mb-1">Breakfast</div>
                                <div className="text-white/40 text-xs">No meal planned</div>
                                <div className="mt-3 w-8 h-8 flex items-center justify-center">
                                  <span className="text-white/20">🍽</span>
                                </div>
                                <button className="mt-2 bg-white/10 text-white/70 text-xs px-2 py-1 rounded-full">
                                  Add Meal
                                </button>
                              </div>
                              
                              {/* Lunch */}
                              <div className="bg-white/5 border border-white/10 rounded-lg p-3 relative overflow-hidden">
                                <div className="bg-white/10 text-xs rounded-full px-2 py-0.5 absolute top-2 left-2">
                                  Lunch
                                </div>
                                <div className="flex justify-center mt-5">
                                  <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-teal-500/20 to-blue-500/20 flex items-center justify-center">
                                    <span className="text-lg">🥗</span>
                                  </div>
                                </div>
                                <div className="text-center mt-2">
                                  <div className="text-white text-xs font-medium">Mediterranean</div>
                                  <div className="text-white/60 text-xs mt-1">480 cal • 35 min</div>
                                </div>
                              </div>
                              
                              {/* Dinner */}
                              <div className="bg-white/5 border border-white/10 rounded-lg p-3 relative overflow-hidden">
                                <div className="bg-white/10 text-xs rounded-full px-2 py-0.5 absolute top-2 left-2">
                                  Dinner
                                </div>
                                <div className="flex justify-center mt-5">
                                  <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-red-500/20 to-orange-500/20 flex items-center justify-center">
                                    <span className="text-lg">🍝</span>
                                  </div>
                                </div>
                                <div className="text-center mt-2">
                                  <div className="text-white text-xs font-medium">Italian</div>
                                  <div className="text-white/60 text-xs mt-1">490 cal • 45 min</div>
                                </div>
                              </div>
                            </div>
                            
                            {/* Mini Calendar */}
                            <div className="mt-4 bg-white/5 rounded-lg p-2">
                              <div className="flex justify-between items-center mb-2">
                                <div className="text-white/80 text-xs">April 2025</div>
                                <div className="flex space-x-1">
                                  <span className="text-white/40 text-xs">◀</span>
                                  <span className="text-white/40 text-xs">▶</span>
                                </div>
                              </div>
                              <div className="grid grid-cols-7 gap-1">
                                {['S','M','T','W','T','F','S'].map((day, i) => (
                                  <div key={i} className="text-white/40 text-xs text-center">{day}</div>
                                ))}
                                {[...Array(30)].map((_, i) => (
                                  <div 
                                    key={i} 
                                    className={`text-xs text-center rounded-full w-6 h-6 mx-auto flex items-center justify-center
                                      ${i+1 === 27 ? 'bg-soft-pink text-pri-dark' : 'text-white/60'}`}
                                  >
                                    {i+1}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    </div>
                  </div>
                  
                  {/* Step 4 with Illustration */}
                  <div className="bg-gradient-to-br from-pri-dark/80 to-sec-dark/95 rounded-xl overflow-hidden shadow-lg shadow-soft-pink/20">
                    <div className="p-6">
                      <div className="flex items-start mb-6 justify-start">
                        <div className="flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-full bg-mint-green text-pri-dark font-bold">
                          04
                        </div>
                        <div className="ml-4">
                          <h3 className="text-lg font-semibold text-soft-pink">Shop and Cook with Ease</h3>
                          <p className="text-pri-gray mt-1">Get detailed recipes and auto-generated shopping lists.</p>
                        </div>
                      </div>
                      
                      {/* Shopping List Illustration - Categorized version */}
                      <motion.div 
                        className="mt-4 flex justify-center"
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.3, duration: 0.5 }}
                      >
                        <div className="relative w-full max-w-md bg-pri-dark/60 rounded-xl p-4 shadow-lg border border-white/10 overflow-hidden">
                          <div className="absolute top-0 right-0 w-32 h-32 bg-soft-pink/10 rounded-full blur-xl -mr-16 -mt-16"></div>
                          <div className="absolute bottom-0 left-0 w-24 h-24 bg-mint-green/10 rounded-full blur-xl -ml-10 -mb-10"></div>
                          
                          {/* Dairy & Eggs Section */}
                          <div className="mb-3">
                            <div className="flex items-center justify-between bg-white/5 rounded-t-lg px-3 py-2">
                              <div className="flex items-center">
                                <span className="text-white font-medium text-sm">Dairy & Eggs</span>
                                <span className="ml-2 bg-soft-pink/30 text-white/80 text-xs px-2 py-0.5 rounded-full">3 items</span>
                              </div>
                              <div className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center">
                                <span className="text-white text-xs">∧</span>
                              </div>
                            </div>
                            
                            <div className="border-t border-white/5">
                              {[
                                { name: "feta cheese", amount: "50 g" },
                                { name: "fresh mozzarella cheese", amount: "100 g" },
                                { name: "mozzarella cheese", amount: "100 g" }
                              ].map((item, i) => (
                                <div key={i} className="flex items-center justify-between px-3 py-2 border-b border-white/5">
                                  <div className="flex items-center">
                                    <div className="w-4 h-4 rounded-full bg-white/20 mr-3"></div>
                                    <span className="text-white/80 text-sm">{item.name}</span>
                                  </div>
                                  <div className="flex items-center">
                                    <span className="text-white/60 text-xs mr-2">{item.amount}</span>
                                    <div className="w-5 h-5 rounded bg-white/10 flex items-center justify-center">
                                      <span className="text-white/60 text-xs">✎</span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                          
                          {/* Meat Section */}
                          <div className="mb-3">
                            <div className="flex items-center justify-between bg-white/5 rounded-t-lg px-3 py-2">
                              <div className="flex items-center">
                                <span className="text-white font-medium text-sm">Meat</span>
                                <span className="ml-2 bg-soft-pink/30 text-white/80 text-xs px-2 py-0.5 rounded-full">2 items</span>
                              </div>
                              <div className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center">
                                <span className="text-white text-xs">∧</span>
                              </div>
                            </div>
                            
                            <div className="border-t border-white/5">
                              {[
                                { name: "chicken breast", amount: "600 g" },
                                { name: "salmon fillets", amount: "300 g" }
                              ].map((item, i) => (
                                <div key={i} className="flex items-center justify-between px-3 py-2 border-b border-white/5">
                                  <div className="flex items-center">
                                    <div className="w-4 h-4 rounded-full bg-white/20 mr-3"></div>
                                    <span className="text-white/80 text-sm">{item.name}</span>
                                  </div>
                                  <div className="flex items-center">
                                    <span className="text-white/60 text-xs mr-2">{item.amount}</span>
                                    <div className="w-5 h-5 rounded bg-white/10 flex items-center justify-center">
                                      <span className="text-white/60 text-xs">✎</span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    </div>
                  </div>
                  
                  {/* Step 5 with Illustration */}
                  <div className="bg-gradient-to-br from-pri-dark/80 to-sec-dark/95 rounded-xl overflow-hidden shadow-lg shadow-soft-pink/20">
                    <div className="p-6">
                      <div className="flex items-start mb-6 justify-start">
                        <div className="flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-full bg-mint-green text-pri-dark font-bold">
                          05
                        </div>
                        <div className="ml-4">
                          <h3 className="text-lg font-semibold text-soft-pink">Add to Online Shop</h3>
                          <p className="text-pri-gray mt-1">Send your grocery list directly to your favorite online supermarket.</p>
                        </div>
                      </div>
                      
                      {/* Online Shopping Illustration */}
                      <motion.div 
                        className="mt-4 flex justify-center"
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.3, duration: 0.5 }}
                      >
                        <div className="relative w-full max-w-md bg-pri-dark/60 rounded-xl p-6 shadow-lg border border-white/10 overflow-hidden">
                          <div className="absolute top-0 right-0 w-32 h-32 bg-soft-pink/10 rounded-full blur-xl -mr-16 -mt-16"></div>
                          <div className="absolute bottom-0 left-0 w-24 h-24 bg-mint-green/10 rounded-full blur-xl -ml-10 -mb-10"></div>
                          
                          <div className="flex justify-between mb-4">
                            <div className="text-white text-sm font-medium">Connect to Online Shops</div>
                            <div className="flex gap-2">
                              <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center">
                                <span className="text-white text-xs">🛒</span>
                              </div>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-3 gap-3 mb-4">
                            {[
                              "Tesco",
                              "Sainsbury's",
                              "Waitrose", 
                              "ASDA",
                              "Ocado",
                              "More..."
                            ].map((shop, i) => (
                              <div key={i} className="bg-white/5 rounded-lg p-3 flex items-center justify-center">
                                <span className="text-white/80 text-sm">{shop}</span>
                              </div>
                            ))}
                          </div>
                          
                          <div className="mt-4 p-3 bg-soft-pink/20 rounded-lg relative">
                            <div className="absolute -top-3 -right-3 bg-soft-pink text-pri-dark text-xs font-semibold px-3 py-1 rounded-full shadow-md">
                              Coming Soon
                            </div>
                            <div className="flex items-center mb-2">
                              <div className="w-3 h-3 rounded-full bg-soft-pink mr-2"></div>
                              <div className="text-white text-sm">One-click checkout</div>
                            </div>
                            <div className="flex justify-between items-center">
                              <div className="text-white/60 text-xs">5 items • Tesco • £28.50</div>
                              <button className="bg-mint-green text-pri-dark text-xs px-3 py-1 rounded-lg">
                                Checkout
                              </button>
                            </div>
                          </div>
                          
                          <div className="mt-4 pt-4 border-t border-white/10">
                            <div className="text-white/60 text-xs flex items-center">
                              <span className="text-mint-green mr-2">✓</span>
                              Automatic substitution preferences
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Special Features section */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.h2 
              className="text-3xl font-bold mb-12 text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Special Features
            </motion.h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[
                {
                  title: "Smart Recipe Substitutions",
                  description: "Our AI can intelligently suggest ingredient substitutions based on what you have available.",
                  comingSoon: false
                },
                {
                  title: "Calendar Integration",
                  description: "Connect with our smart calendar to plan meals around your schedule and commitments.",
                  comingSoon: false
                },
                {
                  title: "Nutritional Analysis",
                  description: "Get detailed nutritional information for every meal and track your nutrient intake.",
                  comingSoon: false
                },
                {
                  title: "Fully Automated Grocery Shopping",
                  description: "AI automatically orders your groceries based on your meal plan with preferred supermarkets.",
                  comingSoon: true
                },
                {
                  title: "Shared Meal Planning",
                  description: "Share your meal plan in a calendar view with family and friends for collaborative planning.",
                  comingSoon: true
                }
              ].map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 * index }}
                  className="bg-gradient-to-br from-pri-dark/80 to-sec-dark/90 p-6 rounded-xl border border-white/10 hover:border-white/20 transition-all duration-300 relative"
                >
                  {feature.comingSoon && (
                    <div className="absolute -top-3 -right-3 bg-soft-pink text-pri-dark text-xs font-semibold px-3 py-1 rounded-full shadow-md">
                      Coming Soon
                    </div>
                  )}
                  <h3 className="text-xl font-semibold mb-3 text-soft-pink">{feature.title}</h3>
                  <p className="text-pri-gray mb-4">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA section */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-gradient-to-r from-soft-pink/20 to-mint-green/20 rounded-2xl p-8 md:p-12 text-center"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Simplify Meal Planning?</h2>
              <p className="text-lg text-pri-gray mb-8 max-w-2xl mx-auto">
                Join Jingleez today and let our AI take the stress out of planning your meals.
              </p>
              <Link
                to="/meal-planner"
                className="inline-flex items-center bg-mint-green text-pri-dark px-8 py-3 rounded-full font-semibold hover:bg-mint-green/90 transition-colors"
              >
                Try Meal Planner
              </Link>
            </motion.div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-pri-dark py-8 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="mb-4 md:mb-0">
              <img
                className="h-8 w-auto"
                src={`${process.env.REACT_APP_CDN_URL}/logo/logo-no-bg.png`}
                alt="Jingleez"
              />
            </div>
            <div className="flex space-x-8">
              <Link to="/" className="text-pri-gray hover:text-white transition-colors">
                Home
              </Link>
              <Link to="/about" className="text-pri-gray hover:text-white transition-colors">
                About
              </Link>
              <Link to="/contact" className="text-pri-gray hover:text-white transition-colors">
                Contact
              </Link>
            </div>
          </div>
          <div className="mt-8 pt-4 border-t border-white/10 text-center">
            <p className="text-pri-gray text-sm">© {new Date().getFullYear()} Jingleez. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
} 